import React, { useEffect, useRef, useState } from 'react';
import { MapPin, X } from 'lucide-react';
import { usePractice } from '../context/PracticeContext';
import './move-banner.css';

const DISMISS_KEY = 'move_banner_dismissed_v1';
const ROTATE_MS = 6000;
const MESSAGES = [
  'We Are Moving to Waterloo from Monday 26th October',
  'Our New Address: 121 Westminster Bridge Road, London, SE1 7HR',
  'Same Great Care, New Location — See You in Waterloo',
];

const readDismissed = () => {
  try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
};

const useAnnounceHeight = (ref, visible) => {
  useEffect(() => {
    const root = document.documentElement;
    if (!visible || !ref.current) {
      root.style.setProperty('--announce-h', '0px');
      return undefined;
    }
    const sync = () => root.style.setProperty('--announce-h', `${ref.current.offsetHeight}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(ref.current);
    return () => { ro.disconnect(); root.style.setProperty('--announce-h', '0px'); };
  }, [ref, visible]);
};

const MoveAnnouncementBanner = () => {
  const { announcement_enabled: enabled } = usePractice();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [index, setIndex] = useState(0);
  const ref = useRef(null);
  const visible = enabled && !dismissed;
  useAnnounceHeight(ref, visible);

  useEffect(() => {
    if (!visible) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % MESSAGES.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div ref={ref} className="move-banner" role="region" aria-label="Clinic move announcement" data-testid="move-banner">
      <span className="move-banner-tag" aria-hidden="true"><MapPin size={14} strokeWidth={2.5} /> <span className="move-banner-tag-label">Moving</span></span>
      <div className="move-banner-track" aria-live="polite">
        {MESSAGES.map((msg, i) => (
          <p
            key={msg}
            className={`move-banner-msg ${i === index ? 'is-active' : ''}`}
            aria-hidden={i !== index}
            data-testid={`move-banner-msg-${i}`}
          >
            {msg}
          </p>
        ))}
      </div>
      <div className="move-banner-dots" aria-hidden="true">
        {MESSAGES.map((msg, i) => <span key={msg} className={i === index ? 'is-active' : ''} />)}
      </div>
      <button type="button" className="move-banner-close" onClick={dismiss} aria-label="Dismiss announcement" data-testid="move-banner-dismiss">
        <X size={18} strokeWidth={2.5} />
      </button>
    </div>
  );
};

export default MoveAnnouncementBanner;
